/**
 * Copyright 2018 Samsung Electronics All Rights Reserved
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { IErrorInterceptor, IResponse, IRequestInterceptor } from 'frest';
import { AuthStorage } from '@swuif/auth';

export const sbaasLegacyRequestInterceptor = (
  authStorage: AuthStorage,
): IRequestInterceptor => async ({ request }) => {
  const credential = await authStorage.load();
  if (credential) {
    request.headers.set('X-Sb-Token', credential.key);
  }
  return request;
};

export const sbaasLegacyAuthErrorInterceptor = (
  storage: AuthStorage,
): IErrorInterceptor => err =>
  new Promise<IResponse<any> | null | undefined>((resolve, reject) => {
    const { frest, request, response } = err;
    if (response && response.data && response.status === 401) {
      if (request.retry == null) {
        request.retry = 0;
      }
      if (request.retry >= 3) {
        storage.store(undefined, true);
        reject(err);
        return;
      }
      if (response.data.sbaas_code === 40105) {
        const sb = response.headers.get('x-sb-token');
        storage
          .load()
          .then(cred => {
            if (cred && sb) {
              cred.key = sb;
              return storage.store(cred);
            }
            return Promise.reject(err);
          })
          .then(() => {
            setTimeout(() => {
              request.retry += 1;
              frest
                .request(request)
                .then(resolve)
                .catch(e => {
                  storage.store(undefined, true);
                  reject(e);
                });
            }, 1000);
          });
        return;
      } else if (response.data.sbaas_code !== 40104) {
        // this will trigger AuthProvider to rerender
        // with `isAuthenticated` set to false, so that
        // route will be redirected to login
        storage.store(undefined, true);
        reject(err);
        return;
      }
    }
    resolve(null);
  });
